import { createContext, useContext } from 'react';

/**
 * Promise-based replacement for window.prompt().
 *
 * Same shape as confirmContext: App owns the state and renders the dialog, and
 * publishes its `prompt` function here. The state cannot live in a provider
 * component App renders, because context reaches DESCENDANTS only — App's own
 * call would read the default below and every prompt would silently return
 * null. App calls its in-scope `prompt`; children use this hook.
 *
 * Text mode:
 *
 *   const prompt = usePrompt();
 *   const name = await prompt({ title: 'Rename clip', defaultValue: current });
 *   if (!name) return;                       // null === cancelled
 *
 * Choice mode — pass choices instead of asking for typed input:
 *
 *   const keepId = await prompt({
 *     title: 'Which record do you want to keep?',
 *     choices: people.map(p => ({ value: p.id, label: p.name })),
 *   });
 *   if (keepId == null) return;
 *
 * Choice mode returns the chosen `value` directly, which is the point: the old
 * numbered-text prompts required parseInt round trips that produced NaN and
 * off-by-one bugs.
 *
 * Resolves null on cancel, matching window.prompt, so existing
 * `if (!answer) return;` guards continue to work.
 */
const PromptContext = createContext(async () => null);

export const PromptProvider = PromptContext.Provider;

export function usePrompt() {
  return useContext(PromptContext);
}

export default PromptContext;
